// Copyright (c) 2020 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import * as React from "react";
import { connect } from "react-redux";
import { RoutedProps, withRouter } from "view/reactrouterinterceptor";

import { MainPanel } from "types";
import { isSet } from "shared/utils/utils";
import { IState } from "states/states";
import { IGeoBounds } from "model/site";

import SitesList from "view/comp/SitesList";
import SiteDetails from "view/comp/SiteDetails";

import "./SiteSelection.scss";

import cssContent from "constants.module.scss";

declare const IS_CLOUD: boolean;

// keep Leaflet and the tile renderer out of the main bundle; linx devices never show the map
// (single auto-selected site, tiles only allow cloud origins)
const SiteMap = IS_CLOUD ? React.lazy(() => import("view/comp/SiteMap")) : undefined;

// below, there is no room for the map next to the list
const mapLayoutQuery = `(min-width: ${cssContent.GRID_BREAKPOINT_LG})`;

interface ISiteSelectionProps {
  selectedSite: number | undefined;
  // site whose details were opened from the list or map (?site= url parameter)
  detailsSite: number | undefined;
}

const mapStateToProps = (state: IState) => {
  const props: ISiteSelectionProps = {
    selectedSite: state.cached.selectedSite,
    detailsSite: state.router.site,
  };
  return props;
};

interface ISiteSelectionExtProps {
  // show the connected site's details (MainPanel.SITE_INFO) instead of the site list
  showConnectedSite?: boolean;
}

interface ISiteSelectionState {
  // the map is only shown (and mounted) if the window is wide enough
  isMapLayoutWide: boolean;
  // the sidebar covers this part of the map
  panelWidthPx: number;
  // last "Show in map" click; a new object per click, so re-picking the same site re-centers it
  mapFocusRequest?: { id: number };
  // which site's popup is currently open on the map, if any
  openPopupSiteId?: number;
  // overrides detailsSite the instant an auto-switch (see handlePopupOpenChange) is triggered,
  // until the route catches up - avoids a one-frame flash of stale state (e.g. the show-in-map
  // button) while that navigation is still in flight
  autoSwitchTargetId?: number;
  // shared by list and map, debounced by the search field
  filterText: string;
  // map filters, only applied while the map is shown; kept here since SitesList unmounts for
  // the details view
  onlyShowInView: boolean;
  onlyShowWithoutCoordinates: boolean;
  viewportBounds?: IGeoBounds;
}

class SiteSelectionComponent extends React.Component<
  ISiteSelectionProps & ISiteSelectionExtProps & RoutedProps,
  ISiteSelectionState
> {
  state: ISiteSelectionState = {
    isMapLayoutWide: window.matchMedia(mapLayoutQuery).matches,
    panelWidthPx: 0,
    filterText: "",
    onlyShowInView: false,
    onlyShowWithoutCoordinates: false,
  };

  private mapLayoutMql = window.matchMedia(mapLayoutQuery);
  private onMapLayoutChange = (event: MediaQueryListEvent) => {
    this.setState({ isMapLayoutWide: event.matches });
  };

  // replace, so toggling details doesn't fill the browser history
  private handleShowDetails = (id: number) => {
    this.props.navigate(`../${MainPanel.SITES}?site=${id}`, { replace: true });
  };

  private handleHideDetails = () => {
    this.props.navigate(`../${MainPanel.SITES}`, { replace: true });
  };

  private panelRef = React.createRef<HTMLDivElement>();
  private panelResizeObserver = new ResizeObserver(([entry]) => {
    this.setState({ panelWidthPx: entry.contentRect.width });
  });

  // stable callbacks, so the memoized SitesList and SiteMap skip unrelated re-renders
  private handleShowInMap = (id: number) => this.setState({ mapFocusRequest: { id } });
  private handleFilterTextChange = (filterText: string) => this.setState({ filterText });
  private handleOnlyShowInViewChange = (onlyShowInView: boolean) =>
    this.setState({ onlyShowInView });
  private handleOnlyShowWithoutCoordinatesChange = (onlyShowWithoutCoordinates: boolean) =>
    this.setState({ onlyShowWithoutCoordinates });
  private handleViewportBoundsChange = (viewportBounds: IGeoBounds) =>
    this.setState({ viewportBounds });
  // the site a currently open details view shows, including a pending auto-switch
  private get effectiveDetailsSiteId(): number | undefined {
    return isSet(this.props.detailsSite) ?
        (this.state.autoSwitchTargetId ?? this.props.detailsSite)
      : undefined;
  }

  private handlePopupOpenChange = (openPopupSiteId: number | undefined) => {
    const currentDetailsSiteId = this.effectiveDetailsSiteId;
    // keep an open details view in sync with whichever marker's popup is opened on the map
    if (
      isSet(openPopupSiteId) &&
      isSet(currentDetailsSiteId) &&
      openPopupSiteId !== currentDetailsSiteId
    ) {
      this.handleShowDetails(openPopupSiteId);
      this.setState({ openPopupSiteId, autoSwitchTargetId: openPopupSiteId });
    } else {
      this.setState({ openPopupSiteId });
    }
  };

  componentDidMount() {
    this.mapLayoutMql.addEventListener("change", this.onMapLayoutChange);
    if (this.panelRef.current) {
      // measure now, as the ResizeObserver reports too late for the map's initial fit
      this.setState({ panelWidthPx: this.panelRef.current.getBoundingClientRect().width });
      this.panelResizeObserver.observe(this.panelRef.current);
    }
  }

  componentWillUnmount() {
    this.mapLayoutMql.removeEventListener("change", this.onMapLayoutChange);
    this.panelResizeObserver.disconnect();
  }

  componentDidUpdate() {
    if (
      isSet(this.state.autoSwitchTargetId) &&
      this.state.autoSwitchTargetId === this.props.detailsSite
    ) {
      this.setState({ autoSwitchTargetId: undefined });
    }
  }

  private renderContent(showMap: boolean) {
    // the maintenance filter hides every marker, so there'd be nothing to focus
    const canShowInMap = showMap && !this.state.onlyShowWithoutCoordinates;
    if (this.props.showConnectedSite) {
      const siteId = this.props.selectedSite;
      return (
        <SiteDetails
          siteId={siteId}
          onBack={() => this.props.navigate(`../${MainPanel.SITES}`)}
          onShowInMap={
            canShowInMap && isSet(siteId) ? () => this.handleShowInMap(siteId) : undefined
          }
          isPopupOpenInMap={this.state.openPopupSiteId === siteId}
        />
      );
    }
    const siteId = this.effectiveDetailsSiteId;
    if (isSet(siteId)) {
      return (
        <SiteDetails
          siteId={siteId}
          onBack={this.handleHideDetails}
          onShowInMap={canShowInMap ? () => this.handleShowInMap(siteId) : undefined}
          isPopupOpenInMap={this.state.openPopupSiteId === siteId}
        />
      );
    }
    const onlyShowInView = showMap && this.state.onlyShowInView;
    return (
      <SitesList
        onDetails={this.handleShowDetails}
        onShowInMap={showMap ? this.handleShowInMap : undefined}
        filterText={this.state.filterText}
        onFilterTextChange={this.handleFilterTextChange}
        canFilterByView={showMap}
        onlyShowInView={onlyShowInView}
        onOnlyShowInViewChange={this.handleOnlyShowInViewChange}
        onlyShowWithoutCoordinates={showMap && this.state.onlyShowWithoutCoordinates}
        onOnlyShowWithoutCoordinatesChange={this.handleOnlyShowWithoutCoordinatesChange}
        // only when needed, so map moves don't re-render the list otherwise
        viewportBounds={onlyShowInView ? this.state.viewportBounds : undefined}
      />
    );
  }

  public render() {
    const isList = !this.props.showConnectedSite && !isSet(this.props.detailsSite);
    const showMap = SiteMap !== undefined && this.state.isMapLayoutWide;
    // details shown in the panel: the connected site's on SITE_INFO, else the ones opened from list/map
    const shownDetailsSite =
      this.props.showConnectedSite ? this.props.selectedSite : this.props.detailsSite;
    return (
      <div id="siteSelection" className={`row${showMap ? " hasMapBackground" : ""}`}>
        {/* full-screen background; the sidebar below floats on top of it */}
        {showMap && SiteMap && (
          <div className="siteSelectionMapBackground">
            <React.Suspense fallback={null}>
              <SiteMap
                leftOffsetPx={this.state.panelWidthPx}
                focusRequest={this.state.mapFocusRequest}
                filterText={this.state.filterText}
                onViewportBoundsChange={this.handleViewportBoundsChange}
                onDetails={this.handleShowDetails}
                detailsSiteId={shownDetailsSite}
                onHideDetails={this.handleHideDetails}
                onlyShowWithoutCoordinates={this.state.onlyShowWithoutCoordinates}
                onPopupOpenChange={this.handlePopupOpenChange}
              />
            </React.Suspense>
          </div>
        )}
        <div className="siteSelectionScroll">
          {/* the list fills the screen height, the details only as far as needed */}
          <div ref={this.panelRef} className={`siteSelectionPanel${isList ? " h-100" : ""}`}>
            {this.renderContent(showMap)}
          </div>
        </div>
      </div>
    );
  }
}

export default connect(mapStateToProps)(withRouter(SiteSelectionComponent));
